// Phase 7 · shipment changes (04 §4.3, §4.6; 06 §6.3, §6.8; 12; 15). THE CONTRACT for the office
// screens that amend, cancel, refund, charge at the destination and merge customers.
//
// What is defined where (all exported from `@ecobox/schema`):
//
//   shipmentChanges.ts  (this file)
//     enums        DESTINATION_CHARGE_KINDS, REFUND_METHODS, REFUND_KINDS, SHIPMENT_CHANGE_KINDS
//     documents    refundDocSchema            `refunds/{rfd_…}`          one per refund, cancel-with-money or credit
//                  shipmentChangeDocSchema    `shipmentChanges/{shc_…}`  the change log of a shipment (amend, cancel, charge, refund)
//                  customerMergeDocSchema     `customerMerges/{mrg_…}`   one per merge, with its progress
//                  destinationChargeSchema    an entry of `shipments.destinationCharges[]`
//                  cancellationSchema         `shipments.cancellation`
//   commands/changes.ts  the five commands: amendShipment, cancelShipment, refundPayment,
//                  addDestinationCharge (staff, scope-checked) and mergeCustomers (`customers.merge`)
//
// Additions to documents that already exist (all optional, so older documents still parse):
//
//   shipments            addOnCodes, discountMinor (what the sale was priced with, for a re-price),
//                        destinationAccruedMinor (destination charges booked), destinationCharges[],
//                        removedBarcodes[], cancellation, lastChangeAt; `invoice.lines[]` may carry a
//                        `destination_charge` line with `currency`, `chargeId` and `chargeKind`.
//                        `money.status` keeps describing the FREIGHT. A destination charge raises
//                        `invoice.grossMinor` and `money.balanceMinor` (what the door collects) and
//                        leaves `money.status` alone, so a shipment with only a charge due is not "unpaid".
//   payments             `method: 'voucher'` (the voucher code is in `voucherCode`), refundedMinor,
//                        refundedDepositMinor
//   vouchers             minSpendMinor, appliedAt, appliedBy, appliedMinor, restoredAt
//   invoices             debitedMinor (debit notes issued against the invoice)
//   exceptions           subject kind `customer`; `customerIds[]` on a `duplicate_customer`
//   customers            nameKey, mergedInto, mergedAt (a merged customer stays as a record)
//   rewardsLedger        type `cancel_pending` (points earned for a shipment that was cancelled)
//   notifications        templates shipment_amended, shipment_cancelled, destination_charge, refund_issued
//
// Rules: staff read `refunds`, `shipmentChanges` and `customerMerges` inside their scope (every
// document carries `branchId` and `sectorId`). Nothing here is written by a client.
import { z } from 'zod';
import { trackingNoProblem } from '@ecobox/domain';
import { branchCodeSchema, currencySchema, sectorCodeSchema } from './config';

// The same patterns as `cargo.ts`, written out here because `cargo.ts` imports this file.
const trackingNoSchema = z
  .string()
  .regex(/^[A-Z0-9][A-Z0-9-]{2,18}[A-Z0-9]$/, 'Tracking number like RUH260042171')
  .superRefine((value, ctx) => {
    const why = trackingNoProblem(value);
    if (why) ctx.addIssue({ code: 'custom', message: why });
  });
const customerIdSchema = z.string().regex(/^cus_[0-9A-HJKMNP-TV-Z]{26}$/);

const minor = z.number().int().nonnegative().max(1e12);
const iso = z.string();

// ---- Enums -------------------------------------------------------------------------------------

/** What a destination charge is for (04 §4.6). `customs_duty` is passed through (2040); the rest is revenue (4010). */
export const DESTINATION_CHARGE_KINDS = [
  'remote_area',
  'storage',
  'redelivery',
  'customs_duty',
  'handling',
  'other',
] as const;
export type DestinationChargeKind = (typeof DESTINATION_CHARGE_KINDS)[number];

export const DESTINATION_CHARGE_LABELS: Record<DestinationChargeKind, string> = {
  remote_area: 'Remote area delivery',
  storage: 'Storage',
  redelivery: 'Re-delivery',
  customs_duty: 'Customs duties and taxes',
  handling: 'Handling fee',
  other: 'Other destination charge',
};

/**
 * How the customer gets a refund. `cash`, `card`, `bank` and `cheque` take the money out of a wallet
 * (`walletId`: the till, card machine or bank account whose account matches); `credit` keeps it as
 * customer credit (2010) and needs no wallet.
 */
export const REFUND_METHODS = ['cash', 'card', 'bank', 'cheque', 'credit'] as const;
export type RefundMethod = (typeof REFUND_METHODS)[number];

/** Why a refund exists. */
export const REFUND_KINDS = ['cancellation', 'overpayment', 'dispute', 'voucher_restored'] as const;
export type RefundKind = (typeof REFUND_KINDS)[number];

export const SHIPMENT_CHANGE_KINDS = ['amend', 'cancel', 'refund', 'destination_charge'] as const;
export type ShipmentChangeKind = (typeof SHIPMENT_CHANGE_KINDS)[number];

// ---- Documents -----------------------------------------------------------------------------------

/** An entry of `shipments.destinationCharges[]`: a charge the consignee owes in the destination currency. */
export const destinationChargeSchema = z
  .object({
    id: z.string(),
    kind: z.enum(DESTINATION_CHARGE_KINDS),
    label: z.string().max(160),
    amountMinor: minor,
    currency: currencySchema,
    note: z.string().max(300),
    /** 2040 for customs duties (passed through), else 4010. */
    creditAccount: z.enum(['4010', '2040']),
    journalId: z.string(),
    addedBy: z.string(),
    addedAt: iso,
  })
  .strict();
export type DestinationCharge = z.infer<typeof destinationChargeSchema>;

/** `shipments.cancellation`, written by `cancelShipment`. */
export const cancellationSchema = z
  .object({
    reason: z.string().max(300),
    at: iso,
    by: z.string(),
    /** Money handed back to the customer from a wallet. */
    refundedMinor: minor,
    /** Kept as customer credit (2010). */
    creditedMinor: minor,
    /** Paid by voucher: the voucher was restored instead. */
    voucherMinor: minor,
    refundId: z.string().optional(),
    /** The credit note that reversed the invoice (absent when no invoice document had been issued). */
    creditNoteId: z.string().optional(),
    journalIds: z.array(z.string()),
    commandId: z.string(),
  })
  .strict();
export type Cancellation = z.infer<typeof cancellationSchema>;

/** `refunds/{rfd_…}`: money (or credit) going back to a customer. Server-written. */
export const refundDocSchema = z.object({
  id: z.string(),
  kind: z.enum(REFUND_KINDS),
  shipmentId: trackingNoSchema,
  paymentId: z.string().optional(),
  customerId: customerIdSchema,
  method: z.enum(REFUND_METHODS),
  walletId: z.string().optional(),
  /** What leaves the wallet or is kept as credit. */
  amountMinor: minor,
  currency: currencySchema,
  reason: z.string().max(300),
  /** The credit note that reduced the invoice, when the refund reduces revenue. */
  creditNoteId: z.string().optional(),
  journalIds: z.array(z.string()),
  approvalId: z.string().optional(),
  approvedBy: z.string().optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  by: z.string(),
  commandId: z.string(),
  at: iso,
});
export type RefundDoc = z.infer<typeof refundDocSchema>;

/**
 * `shipmentChanges/{shc_…}`: the change log of a shipment (04 §4.3). One entry per command that
 * changed it, with what changed in words and the money it moved.
 */
export const shipmentChangeDocSchema = z.object({
  id: z.string(),
  shipmentId: trackingNoSchema,
  kind: z.enum(SHIPMENT_CHANGE_KINDS),
  /** The person's note or reason. */
  note: z.string().max(300),
  /** Plain-words lines, one per change: "Destination: Quezon City → Cebu City". */
  summary: z.array(z.string().max(300)).max(40),
  /** The invoice before and after (VAT-inclusive, in the invoice currency); equal when nothing priced changed. */
  grossBeforeMinor: minor,
  grossAfterMinor: minor,
  invoiceVersionBefore: z.number().int().positive(),
  invoiceVersionAfter: z.number().int().positive(),
  journalIds: z.array(z.string()),
  /** Credit and debit notes issued for the change (invoice document ids). */
  noteIds: z.array(z.string()),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  by: z.string(),
  commandId: z.string(),
  at: iso,
});
export type ShipmentChangeDoc = z.infer<typeof shipmentChangeDocSchema>;

/** `customerMerges/{mrg_…}`: one per merge, so a merge that is too big for one pass can be resumed. */
export const customerMergeDocSchema = z.object({
  id: z.string(),
  keepId: customerIdSchema,
  mergeId: customerIdSchema,
  reason: z.string().max(300),
  status: z.enum(['moving', 'done']),
  /** Documents moved so far, by collection. */
  moved: z.record(z.string(), z.number().int().nonnegative()),
  phones: z.array(z.string()),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  by: z.string(),
  commandId: z.string(),
  startedAt: iso,
  finishedAt: iso.optional(),
});
export type CustomerMergeDoc = z.infer<typeof customerMergeDocSchema>;
